import React, { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Users } from 'lucide-react'
import { useI18n } from '../../i18n'
import { Button } from '../ui/Button'
import styles from '../../styles/components/assets/AssetsInsights.module.css'
import {
  buildAccountabilityInventoryUrl,
  type AccountabilityHrefOpts,
} from '../../services/assetSystemAccountabilityFilters'

export type AccountabilityTopOwner = {
  id: string
  name: string
  assetCount: number
}

export type AccountabilityGap = {
  id: string
  kind: string
  count: number
  message?: string
  href?: AccountabilityHrefOpts
}

export type AssetSystemAccountability = {
  assetsTotal: number
  assetsWithOwner: number
  assetsWithoutOwner: number
  assetsLinkedToIS: number
  assetsWithoutIS: number
  informationSystemsTotal: number
  informationSystemsWithOwner: number
  informationSystemsWithoutOwner: number
  topOwners?: AccountabilityTopOwner[]
  gaps?: AccountabilityGap[]
}

type Props = {
  accountability?: AssetSystemAccountability | null
  /** Preferred when embedded on Assets — updates search params in place */
  onApplyFilter?: (opts: AccountabilityHrefOpts) => void
}

const AssetSystemAccountabilityCard: React.FC<Props> = ({
  accountability = null,
  onApplyFilter,
}) => {
  const { t } = useI18n()
  const navigate = useNavigate()

  const hrefFor = (opts: AccountabilityHrefOpts) => buildAccountabilityInventoryUrl(opts)

  const go = (opts: AccountabilityHrefOpts, e?: React.MouseEvent | React.KeyboardEvent) => {
    if (onApplyFilter) {
      e?.preventDefault()
      onApplyFilter(opts)
      return
    }
    e?.preventDefault()
    const href = hrefFor(opts)
    const u = new URL(href, window.location.origin)
    navigate({ pathname: u.pathname, search: u.search, hash: u.hash })
    requestAnimationFrame(() => {
      const id =
        opts.tab === 'information-systems'
          ? 'assets-information-systems'
          : 'assets-inventory-results'
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  const empty = !accountability
  const a = accountability

  const gaps = useMemo(() => {
    if (!a?.gaps?.length) {
      const derived: AccountabilityGap[] = []
      if (a && a.assetsWithoutOwner > 0) {
        derived.push({
          id: 'assets-without-owner',
          kind: 'assets_without_owner',
          count: a.assetsWithoutOwner,
          href: { tab: 'inventory', owner: 'unassigned' },
        })
      }
      if (a && a.assetsWithoutIS > 0) {
        derived.push({
          id: 'assets-without-is',
          kind: 'assets_without_is',
          count: a.assetsWithoutIS,
          href: { tab: 'inventory', informationSystem: 'unlinked' },
        })
      }
      if (a && a.informationSystemsWithoutOwner > 0) {
        derived.push({
          id: 'is-without-owner',
          kind: 'is_without_owner',
          count: a.informationSystemsWithoutOwner,
          href: { tab: 'information-systems', owner: 'unassigned' },
        })
      }
      return derived.slice(0, 5)
    }
    return a.gaps.slice(0, 5)
  }, [a])

  const gapLabel = (gap: AccountabilityGap): string => {
    if (gap.kind === 'assets_without_owner') {
      return t(
        'assets.accountability.gapAssetsWithoutOwner',
        '{count} assets without assigned owner',
      ).replace('{count}', String(gap.count))
    }
    if (gap.kind === 'assets_without_is') {
      return t(
        'assets.accountability.gapAssetsWithoutIS',
        '{count} assets not linked to an Information System',
      ).replace('{count}', String(gap.count))
    }
    if (gap.kind === 'is_without_owner') {
      return t(
        'assets.accountability.gapIsWithoutOwner',
        '{count} Information System without assigned owner',
      ).replace('{count}', String(gap.count))
    }
    return gap.message || `${gap.count}`
  }

  const FilterLink: React.FC<{
    opts: AccountabilityHrefOpts
    className?: string
    ariaLabel?: string
    children: React.ReactNode
  }> = ({ opts, className, ariaLabel, children }) => (
    <Link
      to={hrefFor(opts)}
      className={className}
      aria-label={ariaLabel}
      onClick={(e) => go(opts, e)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          go(opts, e)
        }
      }}
      data-testid="accountability-filter-link"
    >
      {children}
    </Link>
  )

  return (
    <div className={styles.sectionCard} data-testid="asset-system-accountability-card">
      <h2 className={styles.sectionHeader}>
        <Users className={styles.sectionIcon} aria-hidden />
        {t('assets.accountability.title', 'Asset & System Accountability')}
      </h2>

      {empty ? (
        <div className={styles.stackGap}>
          <p className={styles.emptyHint}>
            {t(
              'assets.accountability.emptyHint',
              'Ownership and Information System linkage will appear here once assets are loaded.',
            )}
          </p>
        </div>
      ) : (
        <>
          <div className={styles.lightMetricGrid}>
            <FilterLink
              opts={{ tab: 'inventory', owner: 'assigned' }}
              className={styles.lightMetric}
              ariaLabel={`${t('assets.accountability.assetOwnership', 'Asset ownership')}: ${a!.assetsWithOwner} / ${a!.assetsTotal}`}
            >
              <p className={styles.lightMetricLabel}>
                {t('assets.accountability.assetOwnership', 'Asset ownership')}
              </p>
              <p className={styles.lightMetricValue}>
                {a!.assetsWithOwner} / {a!.assetsTotal}
              </p>
            </FilterLink>
            <FilterLink
              opts={{ tab: 'inventory', owner: 'unassigned' }}
              className={styles.lightMetric}
              ariaLabel={`${t('assets.accountability.assetsWithoutOwner', 'Assets without owner')}: ${a!.assetsWithoutOwner}`}
            >
              <p className={styles.lightMetricLabel}>
                {t('assets.accountability.assetsWithoutOwner', 'Assets without owner')}
              </p>
              <p className={styles.lightMetricValue}>{a!.assetsWithoutOwner}</p>
            </FilterLink>
            <FilterLink
              opts={{ tab: 'inventory', informationSystem: 'unlinked' }}
              className={styles.lightMetric}
              ariaLabel={`${t('assets.accountability.assetsWithoutIS', 'Assets not linked to IS')}: ${a!.assetsWithoutIS}`}
            >
              <p className={styles.lightMetricLabel}>
                {t('assets.accountability.assetsWithoutIS', 'Assets not linked to IS')}
              </p>
              <p className={styles.lightMetricValue}>{a!.assetsWithoutIS}</p>
            </FilterLink>
            <FilterLink
              opts={{ tab: 'information-systems', owner: 'unassigned' }}
              className={styles.lightMetric}
              ariaLabel={`${t('assets.accountability.isWithoutOwner', 'IS without owner')}: ${a!.informationSystemsWithoutOwner}`}
            >
              <p className={styles.lightMetricLabel}>
                {t('assets.accountability.isWithoutOwner', 'IS without owner')}
              </p>
              <p className={styles.lightMetricValue}>{a!.informationSystemsWithoutOwner}</p>
            </FilterLink>
            {a!.informationSystemsTotal > 0 ? (
              <FilterLink
                opts={{ tab: 'information-systems', owner: 'assigned' }}
                className={styles.lightMetric}
                ariaLabel={`${t('assets.accountability.isOwnership', 'IS ownership')}: ${a!.informationSystemsWithOwner} / ${a!.informationSystemsTotal}`}
              >
                <p className={styles.lightMetricLabel}>
                  {t('assets.accountability.isOwnership', 'IS ownership')}
                </p>
                <p className={styles.lightMetricValue}>
                  {a!.informationSystemsWithOwner} / {a!.informationSystemsTotal}
                </p>
              </FilterLink>
            ) : null}
          </div>

          <div className={styles.stackGap}>
            <h3 className={styles.subsectionTitle}>
              {t('assets.accountability.gaps', 'Accountability gaps')}
            </h3>
            {gaps.length === 0 ? (
              <p className={styles.emptyHint}>
                {t(
                  'assets.accountability.allAssigned',
                  'All assets have assigned owners.',
                )}
              </p>
            ) : (
              <ul className={styles.coverageList}>
                {gaps.map((gap) => {
                  const opts: AccountabilityHrefOpts = gap.href || {
                    tab: 'inventory',
                    owner: 'unassigned',
                  }
                  return (
                    <li key={gap.id}>
                      <FilterLink
                        opts={opts}
                        className={styles.coverageGapBtn}
                        ariaLabel={gapLabel(gap)}
                      >
                        {gapLabel(gap)}
                      </FilterLink>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          {a!.topOwners && a!.topOwners.length > 0 ? (
            <div className={styles.stackGap}>
              <h3 className={styles.subsectionTitle}>
                {t('assets.accountability.topOwners', 'Top owners')}
              </h3>
              <div className={styles.miniTableWrap}>
                <table className={styles.miniTable}>
                  <thead>
                    <tr>
                      <th>{t('assets.accountability.owner', 'Owner')}</th>
                      <th className="text-center">
                        {t('assets.accountability.assetCount', 'Assets')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {a!.topOwners.slice(0, 3).map((owner) => (
                      <tr key={owner.id}>
                        <td>
                          <div className="font-medium">{owner.name}</div>
                        </td>
                        <td className="text-center">{owner.assetCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div className={`${styles.divider} ${styles.footerActions}`}>
            <Button
              type="button"
              color="outline"
              size="small"
              className={styles.coverageCtaLink}
              onClick={(e) => go({ tab: 'inventory', owner: 'unassigned' }, e)}
            >
              {t('assets.accountability.viewUnassignedAssets', 'View unassigned assets')}
            </Button>
            <Button
              type="button"
              color="outline"
              size="small"
              className={styles.footerActionsEnd}
              onClick={(e) => go({ tab: 'inventory', owner: 'assigned' }, e)}
            >
              {t('assets.accountability.viewOwnership', 'View ownership')}
            </Button>
            {a!.informationSystemsWithoutOwner > 0 ? (
              <Button
                type="button"
                color="outline"
                size="small"
                onClick={(e) => go({ tab: 'information-systems', owner: 'unassigned' }, e)}
              >
                {t('assets.accountability.viewUnassignedSystems', 'View unassigned systems')}
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  )
}

export default AssetSystemAccountabilityCard
